import { ClaimStatusEnum, ClaimTypeEnum } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { useHandleServerError } from "Infrastructure/Utilities/Forms/Hooks/useHandleServerError";
import { createSelectOptions } from "Infrastructure/Utilities/Helpers/createSelectOptions";
import { orderKeys } from "Order/Api/orderKeys";
import { useCreateClaim } from "Order/Modules/Claim/Api/Mutation/createClaimMutation";
import { isClaimDetailOpen } from "Order/Modules/Claim/Atom/claimDetailOpen";
import {
	getOrderCreateClaimSchema,
	type OrderCreateClaimFormModel,
} from "Order/Modules/Claim/Components/Form/Schema/orderCreateClaimSchema";
import { OrderPaths } from "Order/Routes/Routing";
import { BaseCard } from "Shared/Components/Cards/BaseCard";
import { DatePicker } from "Shared/Components/Form/CustomFormComponent/DatePicker";
import Select from "Shared/Components/Form/CustomFormComponent/Select";
import TextEditor from "Shared/Components/Form/CustomFormComponent/TextEditor";
import { zodResolver } from "@hookform/resolvers/zod";
import Save from "@mui/icons-material/Save";
import { Button, Grid2, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";

type CreateClaimFormProps = {
	orderId: number;
};

const claimResource = Resources.base.order.claim;
const claimSharedource = Resources.base.claim.claimShared;
const selectOptionsResource = Resources.base.claim.claimSelectOptions;

export function CreateClaimForm({ orderId }: CreateClaimFormProps) {
	const [_, setIsClaimOpen] = useAtom(isClaimDetailOpen);
	const { t } = useResource();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const formDefaultValues = {
		orderId: orderId,
		status: ClaimStatusEnum.active,
		type: ClaimTypeEnum.quality,
		fee: 0,
		description: "",
		finalSolution: null,
		dateNextAction: null,
		dateResolved: null,
	};
	const form = useForm<OrderCreateClaimFormModel>({
		resolver: zodResolver(getOrderCreateClaimSchema()),
		defaultValues: formDefaultValues,
	});

	const {
		control,
		formState: { errors },
	} = form;

	const { mutate: createClaim, isPending } = useCreateClaim(orderId);
	const { handleServerError } = useHandleServerError(form);

	const handleCreateClaimSubmit = () => {
		handleServerError(createClaim, {
			onSuccess: () => {
				form.reset(formDefaultValues);
				queryClient.invalidateQueries({
					queryKey: orderKeys.detail(orderId),
				});
				setIsClaimOpen(false);
				navigate(OrderPaths.Claim.path(orderId));
			},
		});
	};

	return (
		<BaseCard
			title={t(claimSharedource.createClaimHeading)}
			sxCardContent={{
				flexGrow: 1,
			}}
			sx={{
				height: "100%",
				display: "flex",
				flexDirection: "column",
			}}
		>
			<Grid2 container spacing={1} size={{ xs: 12 }}>
				<Grid2 size={{ xs: 12, sm: 6 }}>
					<Select
						id="type"
						name="type"
						label={t(claimResource.type)}
						variant="outlined"
						control={control}
						options={createSelectOptions(
							ClaimTypeEnum,
							t,
							selectOptionsResource.claimType,
						)}
						errors={errors}
						fullWidth
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6 }}>
					<DatePicker
						name="dateNextAction"
						label={t(claimResource.dateNextAction)}
						control={control}
						errors={errors}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12 }}>
					<TextEditor
						name="description"
						label={t(claimResource.description)}
						control={control}
						errors={errors}
					/>
				</Grid2>
			</Grid2>
			<Stack direction="row" gap={1} mt={2}>
				<Button
					startIcon={<Save />}
					variant="contained"
					color="primary"
					onClick={handleCreateClaimSubmit}
					loading={isPending}
				>
					{t(claimResource.saveButton)}
				</Button>
			</Stack>
		</BaseCard>
	);
}
